import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { EmptyAssessmentState } from "@/components/layout/EmptyAssessmentState";
import { Button } from "@/components/ui/button";
import { useAssessment } from "@/context/AssessmentContext";
import {
  buildMarkdownReport,
  buildPlainTextReport,
  buildExecutiveSummary,
  getProviderSnapshot,
  getReportRiskAreas,
  evidenceShortNote,
  clarificationRequiredItems
} from "@/lib/assessmentLogic";
import { buildEmailSummary } from "@/lib/emailSummary";
import { formatDate } from "@/lib/timeUtils";
import { ReportToolbar } from "@/components/report/ReportToolbar";
import { ReportSection } from "@/components/report/ReportSection";
import { ReportProviderSnapshot } from "@/components/report/ReportProviderSnapshot";
import { ReportExecutiveSummary } from "@/components/report/ReportExecutiveSummary";
import { ReportReadyOverview } from "@/components/report/ReportReadyOverview";
import { ReportEvidenceTable } from "@/components/report/ReportEvidenceTable";
import { ReportEvidenceLists } from "@/components/report/ReportEvidenceLists";
import { ReportOperationalRisk } from "@/components/report/ReportOperationalRisk";
import { ReportActionPlan } from "@/components/report/ReportActionPlan";
import { ReportProfessionalFindings } from "@/components/report/ReportProfessionalFindings";
import { ReportReadinessActions } from "@/components/report/ReportReadinessActions";
import { ReportManualReview } from "@/components/report/ReportManualReview";
import { ReportLegalReferralMatrix } from "@/components/report/ReportLegalReferralMatrix";
import { ClassifierOutputCard } from "@/components/results/ClassifierOutputCard";
import { ClarificationRequiredList } from "@/components/results/ClarificationRequiredList";
import { SourceVerificationPanel } from "@/components/results/SourceVerificationPanel";

export default function ReportPage() {
  const {
    answers,
    result,
    completedAt,
    assessmentId,
    readinessActions,
    addReadinessAction,
    removeReadinessAction
  } = useAssessment();

  const generatedAt = useMemo(() => formatDate(completedAt), [completedAt]);

  const provider = useMemo(
    () => (result ? getProviderSnapshot(answers, result) : null),
    [answers, result]
  );
  const exec = useMemo(
    () => (result ? buildExecutiveSummary(answers, result) : ""),
    [answers, result]
  );
  const riskAreas = useMemo(
    () => (result ? getReportRiskAreas(result) : []),
    [result]
  );

  const markdown = useMemo(() => {
    if (!result) return "";
    return buildMarkdownReport({
      result,
      answers,
      assessmentId: assessmentId || "\u2014",
      generatedAt,
      readinessActions
    });
  }, [result, answers, assessmentId, generatedAt, readinessActions]);

  const plainText = useMemo(() => {
    if (!result) return "";
    return buildPlainTextReport({
      result,
      answers,
      assessmentId: assessmentId || "\u2014",
      generatedAt,
      readinessActions
    });
  }, [result, answers, assessmentId, generatedAt, readinessActions]);

  const emailSummary = useMemo(() => {
    if (!result || !provider) return "";
    return buildEmailSummary({
      result,
      provider,
      assessmentId: assessmentId || "\u2014",
      generatedAt
    });
  }, [result, provider, assessmentId, generatedAt]);

  if (!completedAt || !result || !provider) {
    return (
      <EmptyAssessmentState
        sectionLabel="Report mode \u00b7 client-ready"
        pageTitle="No readiness report yet"
        message="The Readiness Snapshot Report is generated automatically when you complete the intake. Once complete, this page will produce a clean, client-ready, printable report with the provider snapshot, executive summary, R.E.A.D.Y. summary, full evidence map, operational risk summary, 30 / 60 / 90 plan, and deterministic professional readiness findings."
      />
    );
  }

  return (
    <PageShell>
      <div className="container py-10 lg:py-14">
        <div className="flex flex-col gap-3 report-no-print">
          <p className="eyebrow">Report mode · client-ready</p>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h1 className="font-display text-3xl font-medium leading-tight text-foreground sm:text-4xl">
              Readiness Snapshot Report
            </h1>
            <Button
              asChild
              variant="ghost"
              className="rounded-sm px-3 text-[13px] font-medium text-foreground hover:bg-secondary"
            >
              <Link to="/results">
                <ArrowLeft className="h-4 w-4" />
                Back to snapshot
              </Link>
            </Button>
          </div>
          <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
            NDIS 0138 Readiness is a consultancy readiness tool only. It is not legal advice, an
            Approved Quality Auditor report, an official NDIS Commission tool, a registration
            guarantee, or a compliance certification.
          </p>
        </div>

        <ReportToolbar plainText={plainText} markdown={markdown} emailSummary={emailSummary} />

        <article id="niss0138-report" className="report-document mt-8 space-y-8">
          <header className="report-cover flex flex-col gap-3 border-b border-border pb-6">
            <span className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              NDIS 0138 Readiness · R.E.A.D.Y. v0.1
            </span>
            <h2 className="font-display text-3xl font-medium leading-tight text-foreground sm:text-4xl">
              <FileText className="mr-2 inline-block h-7 w-7 align-[-4px] text-accent" />
              Readiness Snapshot Report
            </h2>
            <dl className="grid grid-cols-2 gap-3 text-sm text-foreground/85 sm:grid-cols-4">
              <div>
                <dt className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Reference</dt>
                <dd className="font-mono-label mt-0.5">{assessmentId || "—"}</dd>
              </div>
              <div>
                <dt className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Generated</dt>
                <dd className="mt-0.5">{generatedAt}</dd>
              </div>
              <div>
                <dt className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Framework</dt>
                <dd className="mt-0.5">R.E.A.D.Y. v0.1</dd>
              </div>
              <div>
                <dt className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Status</dt>
                <dd className="mt-0.5">{provider.manualReviewStatus}</dd>
              </div>
            </dl>
            <p className="max-w-3xl border-l-2 border-accent/40 pl-4 text-sm italic leading-relaxed text-muted-foreground">
              NDIS 0138 Readiness is a consultancy readiness tool only. It is not legal advice, an
              Approved Quality Auditor report, an official NDIS Commission tool, a registration
              guarantee, or a compliance certification.
            </p>
          </header>

          {result.manualReview && (
            <ReportManualReview reasons={result.manualReviewReasons} />
          )}

          <ReportSection number="01" title="Provider Snapshot">
            <ReportProviderSnapshot provider={provider} />
          </ReportSection>

          <ReportSection number="02" title="Classifier Output">
            <ClassifierOutputCard classifier={result.classifier} />
          </ReportSection>

          <ReportSection number="03" title="Source Verification">
            <SourceVerificationPanel verification={result.sourceVerification} />
          </ReportSection>

          <ReportSection number="04" title="Executive Summary">
            <ReportExecutiveSummary text={exec} />
          </ReportSection>

          <ReportSection number="05" title="R.E.A.D.Y. Summary">
            <ReportReadyOverview domains={result.domainRollup} />
          </ReportSection>

          <ReportSection number="06" title="Evidence Readiness Table">
            <ReportEvidenceTable items={result.evidenceItems} />
          </ReportSection>

          <ReportSection number="07" title="Missing Evidence">
            <ReportEvidenceLists
              items={result.missingEvidence}
              shortNote={evidenceShortNote}
              accent="red"
              emptyLabel="No missing or unconfirmed evidence recorded."
            />
          </ReportSection>

          <ReportSection number="08" title="Evidence Needing Review">
            <ReportEvidenceLists
              items={result.reviewEvidence}
              shortNote={evidenceShortNote}
              accent="amber"
              emptyLabel="No items flagged for review."
            />
          </ReportSection>

          <ReportSection number="09" title="Operational Risk Summary">
            <ReportOperationalRisk areas={riskAreas} />
          </ReportSection>

          <ReportSection number="10" title="30 / 60 / 90 Day Plan">
            <ReportActionPlan plan={result.actionPlan} />
          </ReportSection>

            <ReportSection number="11" title="Professional Readiness Findings">
              <ReportProfessionalFindings result={result} />
            </ReportSection>

          <ReportSection number="12" title="Clarification Required">
            <ClarificationRequiredList items={clarificationRequiredItems(result)} framed={false} />
          </ReportSection>

          <ReportSection number="13" title="Readiness Actions">
            <ReportReadinessActions
              actions={readinessActions}
              onAdd={addReadinessAction}
              onRemove={removeReadinessAction}
            />
          </ReportSection>

          <ReportSection number="14" title="Legal & Documentation Referral Triggers">
            <ReportLegalReferralMatrix answers={answers} />
          </ReportSection>

          <footer className="border-t border-border pt-6 text-xs italic leading-relaxed text-muted-foreground">
            End of report. Held in local browser state only. No participant identifiers, addresses,
            medical histories, or worker private information are collected by this tool.
          </footer>
        </article>
      </div>
    </PageShell>
  );
}
